<template>
  <div class="development-user-detail-page">
    <nav id="breadcrumbs" :aria-label="$t('general.page_path')" class="mb-3">
      <ul>
        <li>
          <router-link :to="{ name: 'company.dashboard' }">
            <i class="uil-home-alt"></i>
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'company.development-evaluation.index' }">
            {{ $t('general.development_evaluation') }}
          </router-link>
        </li>
        <li>{{ user ? user.full_name : $t('general.user_detail') }}</li>
      </ul>
    </nav>

    <default-loading v-if="initialLoading"></default-loading>

    <div v-else-if="notFound" class="card empty-state">
      <i class="uil-user-times"></i>
      <h3>{{ $t('general.user_not_found') }}</h3>
      <p>{{ $t('general.user_not_found_hint') }}</p>
      <router-link :to="{ name: 'company.development-evaluation.index' }" class="btn btn-default">
        {{ $t('general.back_to_list') }}
      </router-link>
    </div>

    <template v-else-if="user">
      <section class="card user-summary-card">
        <div class="profile-summary">
          <AvatarOrIcon :alt="user.full_name || $t('general.user')"
                        :size-px="72"
                        :src="user.avatar || ''"
          />

          <div class="profile-heading">
            <h2>{{ user.full_name || '-' }}</h2>
            <p>
              <i class="uil-envelope-alt"></i>
              {{ user.email || '-' }}
            </p>
          </div>

          <div class="competency-total">
            <span>{{ competencyTotal }}</span>
            <small>{{ $t('general.total_competencies') }}</small>
          </div>
        </div>

        <div class="organization-grid">
          <div v-for="item in organizationItems"
               :key="item.label"
               class="organization-item"
          >
            <span>{{ item.label }}</span>
            <strong>{{ item.value || '-' }}</strong>
          </div>
        </div>
      </section>

      <div class="section-header competency-section-header border-0">
        <div class="section-header-left">
          <h3 class="mb-1">{{ $t('general.competencies') }}</h3>
          <p class="text-muted mb-0">{{ $t('general.assigned_competencies_hint') }}</p>
        </div>
      </div>

      <section class="card competencies-section">
        <div class="competencies-toolbar">
          <div class="search-input-wrapper">
            <i class="uil-search"></i>
            <input v-model="search"
                   :aria-label="$t('general.search_competency')"
                   :placeholder="$t('general.search_placeholder')"
                   class="form-control"
                   type="search"
                   @input="scheduleSearch"
                   @keydown.enter.prevent="applyFilters"
            />
          </div>
          <multiple-select-input v-model="selectedSort"
                                 :allowEmpty="false"
                                 :searchable="false"
                                 :showLabels="false"
                                 :values="sortOptions"
                                 class="assignment-sort"
                                 @select="onSortSelect"
          />
        </div>

        <div v-if="errorMessage" class="uk-alert-danger m-3" role="alert" uk-alert>
          <p class="mb-0">{{ errorMessage }}</p>
        </div>

        <default-loading v-if="competenciesLoading"></default-loading>

        <template v-else>
          <div v-if="assignments.length" class="competency-grid">
            <competency-card v-for="assignment in assignments"
                             :key="assignment.id"
                             :assignment="assignment"
                             @select="openCompetency"
            />
          </div>

          <div v-else class="empty-state competencies-empty">
            <i class="uil-award"></i>
            <h4>{{ $t('general.competencies_not_found') }}</h4>
            <p v-if="search">{{ $t('general.competencies_search_empty') }}</p>
            <p v-else>{{ $t('general.no_competencies_assigned') }}</p>
          </div>

          <pagination v-if="totalPages > 1"
                      :pagination="pagination"
                      class="mt-2 mb-3"
                      @changePage="changePage($event)"/>
        </template>
      </section>
    </template>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import AvatarOrIcon from "@/view/components/icons/AvatarOrIcon.vue";
import CompetencyCard from "@/view/components/developmentCompetencies/CompetencyCard.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/assets/components/Pagination.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";

export default {
  name: "DevelopmentEvaluationUserDetail",
  components: {
    AvatarOrIcon,
    CompetencyCard,
    DefaultLoading,
    MultipleSelectInput,
    Pagination,
  },
  props: {
    userId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      user: null,
      assignments: [],
      search: "",
      selectedSort: null,
      page: 1,
      perPage: 15,
      total: 0,
      totalPages: 1,
      initialLoading: true,
      competenciesLoading: false,
      notFound: false,
      errorMessage: "",
      searchTimer: null,
      requestSequence: 0,
    };
  },
  computed: {
    currentUserId() {
      return this.userId || this.$route.params.userId;
    },
    competencyTotal() {
      return Number(this.user?.competencies_count) || this.total;
    },
    sortOptions() {
      return [
        { id: "desc", name: this.$t("general.sort_newest") },
        { id: "asc", name: this.$t("general.sort_oldest") },
      ];
    },
    organizationItems() {
      return [
        { label: this.$t("general.manager"), value: this.user?.parent?.full_name },
        { label: this.$t("profile.gm_region"), value: this.user?.region_name },
        { label: this.$t("profile.unit"), value: this.user?.department_name },
        { label: this.$t("profile.branch"), value: this.user?.branch_name },
        { label: this.$t("users.mission"), value: this.user?.position_name },
        { label: this.$t("profile.title"), value: this.user?.title_name },
      ];
    },
    pagination() {
      return {
        current_page: this.page,
        last_page: this.totalPages,
        per_page: this.perPage,
        total: this.total,
      };
    },
  },
  watch: {
    currentUserId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.page = 1;
        this.loadPage({ initial: true });
      }
    },
  },
  created() {
    this.selectedSort = this.sortOptions[0];
  },
  mounted() {
    this.loadPage({ initial: true });
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.requestSequence += 1;
  },
  methods: {
    async loadPage({ initial = false } = {}) {
      if (!this.currentUserId) {
        this.notFound = true;
        this.initialLoading = false;
        return;
      }

      const requestId = ++this.requestSequence;
      if (initial) {
        this.initialLoading = true;
        this.notFound = false;
      }
      this.competenciesLoading = true;
      this.errorMessage = "";

      try {
        const response = await ApiService.get(
            `api/competencies-evaluation/${this.currentUserId}?${querystring.stringify({
              search: this.search.trim(),
              order_by: "created_at",
              sort: this.selectedSort?.id || "desc",
              page: this.page,
              perPage: this.perPage,
            })}`
        );

        if (requestId !== this.requestSequence) return;

        const payload = response?.data || {};
        // Response: { user: {...}, competencies: { data, meta } }
        const competencies =
            payload.competencies && typeof payload.competencies === "object"
                ? payload.competencies
                : payload;
        const meta = competencies.meta || {};

        if (payload.user && typeof payload.user === "object") {
          this.user = payload.user;
        } else if (!this.user) {
          this.user = { id: this.currentUserId };
        }

        this.assignments = Array.isArray(competencies.data)
            ? competencies.data
            : [];
        this.total = Number(meta.total) || this.assignments.length;
        this.page = Number(meta.current_page) || this.page;
        this.totalPages = Math.max(1, Number(meta.last_page) || 1);
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.assignments = [];
        this.total = 0;
        this.totalPages = 1;

        if (error?.response?.status === 404) {
          this.notFound = true;
          this.user = null;
        } else {
          this.errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              this.$t("general.competencies_load_failed");
        }
      } finally {
        if (requestId === this.requestSequence) {
          this.initialLoading = false;
          this.competenciesLoading = false;
        }
      }
    },
    // "select" eventi v-model güncellemesinden önce tetiklendiği için
    // seçimi burada set edip öyle filtreliyoruz.
    onSortSelect(option) {
      this.selectedSort = option;
      this.applyFilters();
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applyFilters, 350);
    },
    applyFilters() {
      clearTimeout(this.searchTimer);
      this.page = 1;
      this.loadPage();
    },
    changePage(page) {
      page = Number(page);
      if (!page || page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.loadPage();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openCompetency(assignment) {
      if (!assignment?.id) return;
      this.$router.push({
        name: "company.development-evaluation.competency",
        params: {
          userId: String(this.currentUserId),
          userCompetencyId: String(assignment.id),
        },
      });
    },
  },
};
</script>

<style scoped>
.development-user-detail-page {
  width: 100%;
}

.user-summary-card,
.competencies-section {
  border: 1px solid #e5eaf0;
  border-radius: 12px;
}

.user-summary-card {
  overflow: hidden;
}

/* Sıralama dropdown'ının kart dışına taşabilmesi için */
.competencies-section {
  overflow: visible;
}

.profile-summary {
  align-items: center;
  display: flex;
  gap: 18px;
  padding: 22px 24px;
}

.profile-summary .avatar {
  border: 3px solid #e3ebf5;
  flex: 0 0 72px;
}

.profile-heading {
  min-width: 0;
}

.profile-heading h2 {
  color: #273142;
  font-size: 24px;
  margin: 0 0 6px;
}

.profile-heading p {
  color: #718096;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.competency-total {
  align-items: center;
  background: #f2f7ff;
  border: 1px solid #dfeaff;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  min-width: 120px;
  padding: 12px 18px;
}

.competency-total span {
  color: #315fa8;
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
}

.competency-total small {
  color: #65738a;
  font-weight: 600;
  margin-top: 6px;
}

.organization-grid {
  background: #f9fbfd;
  border-top: 1px solid #edf1f5;
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.organization-item {
  background: #fff;
  min-width: 0;
  padding: 15px 20px;
}

.organization-item span {
  color: #8993a3;
  display: block;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.organization-item strong {
  color: #3d4756;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.competency-section-header {
  margin-top: 28px;
}

.competencies-toolbar {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 18px 20px;
}

.search-input-wrapper {
  flex: 1;
  max-width: 560px;
  position: relative;
}

.search-input-wrapper i {
  color: #94a0af;
  left: 13px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.search-input-wrapper input {
  min-height: 42px;
  padding-left: 38px;
}

.assignment-sort {
  flex: 0 0 210px;
  margin: 0;
  max-width: 210px;
}

.assignment-sort :deep(.form-group) {
  margin: 0;
}

.assignment-sort :deep(.multiselect) {
  height: auto !important;
  min-height: 42px !important;
  overflow: visible !important;
}

.assignment-sort :deep(.multiselect--active) {
  position: relative;
  z-index: 30;
}

.assignment-sort :deep(.multiselect__content-wrapper) {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 10px 24px rgba(30, 41, 59, 0.12);
  z-index: 40;
}

.assignment-sort :deep(.multiselect__tags) {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-sizing: border-box;
  height: 42px !important;
  max-height: 42px !important;
  min-height: 42px !important;
  padding: 8px 40px 0 12px;
}

.assignment-sort :deep(.multiselect__select) {
  height: 40px;
}

.assignment-sort :deep(.multiselect__placeholder),
.assignment-sort :deep(.multiselect__single) {
  color: #4a5568 !important;
  font-size: 13px !important;
  line-height: 24px !important;
  margin: 0 0 8px !important;
  overflow: hidden !important;
  padding: 0 !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.assignment-sort :deep(.multiselect--active .multiselect__tags) {
  border-color: #7c94c8;
  box-shadow: 0 0 0 3px rgba(63, 111, 182, 0.1);
}

.competency-grid {
  background: #f8fafc;
  border-top: 1px solid #edf0f4;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 20px;
}

.empty-state {
  color: #778295;
  padding: 56px 20px;
  text-align: center;
}

.empty-state i {
  color: #a9b6c7;
  font-size: 48px;
}

.empty-state h3,
.empty-state h4 {
  color: #3d4756;
  margin: 12px 0 5px;
}

.empty-state .btn {
  margin-top: 10px;
}

.competencies-empty {
  border-top: 1px solid #edf0f4;
}

@media (max-width: 1100px) {
  .competency-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .profile-summary {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 18px;
  }

  .profile-heading {
    flex: 1;
  }

  .competency-total {
    flex: 1 0 100%;
    margin-left: 0;
  }

  .organization-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .competencies-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .search-input-wrapper,
  .assignment-sort {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }

  .competency-grid {
    grid-template-columns: 1fr;
    padding: 14px;
  }
}

@media (max-width: 480px) {
  .organization-grid {
    grid-template-columns: 1fr;
  }
}
</style>
